import type { Action, ActionIntent } from './Action';
import { showApiErrorToast } from '@packages/shared/utils';

/**
 * Form operational mode injected by the form engine into action contexts.
 * Mirrors `FormBlockMode` from compose without creating a cross-package dependency.
 */
export type FormMode = 'create' | 'edit' | 'view';

export interface ActionExecutionContext {
  pk: string;
  row: Record<string, unknown>;
  selectedKeys?: string[];
  /** Full row objects for the current selection (resolved from table data). */
  selectedRows?: Record<string, unknown>[];
  tableQuery?: Record<string, unknown>;
  refreshTable: () => void;
  /** Clears row selection and resets select-all-pages state after bulk actions. */
  clearSelection?: () => void;
  /** True when "Select All Pages" is active (filter-based bulk endpoint). */
  selectAllActive?: boolean;
  /** IDs explicitly unchecked after Select All Pages was activated. */
  deselectedRowIds?: string[];
  /** Server-side total count for the current query/filter. */
  totalCount?: number;
  /** Configured axios instance with auth headers and base URL. */
  axiosInstance?: { get: Function; post: Function; put: Function; delete: Function; request: Function };
  /** File selected via toolbar import input (automation-compatible hidden file input). */
  file?: File;
  /** Parameters passed to data actions (e.g., resolved tokens from params prop). */
  params?: Record<string, string>;
  /** Pre-existing data from the form — passed to supplemental fetch actions so they can
   *  skip redundant API calls and use the data already available in the view. */
  data?: Record<string, unknown>;
  /** Meta bag — auto-passed from ControlRenderer (wizard state, route params, etc.).
   *  For OPEN_COMPOSE_POPOVER from modal columns: `clientAnchor` ({ clientX, clientY }),
   *  `composeMeta` (mode, entity, semanticId), `composePopover` (panelClassName, width). */
  meta?: Record<string, unknown>;
  /** v2 wizard event context — populated by WizardOrchestratorV2 when dispatching to the handler. */
  wizardContext?: WizardEventContextBag;
  /** Block-events scope for the table's page. Undefined when the table has no semanticId or no EventScope. */
  eventScope?: import('@packages/block-events/core').EventScope;
  /** The semantic ID of the table this action belongs to. */
  tableSemanticId?: string;
  /** Form mode that triggered this action (e.g. 'create', 'edit'). Injected by the form engine on submit. */
  mode?: FormMode;
}

/**
 * Typed shape of the wizard event context passed to handler actions.
 * The full type lives in @packages/compose/wizard, but we keep a
 * structural mirror here to avoid a hard dependency.
 */
export interface WizardEventContextBag {
  event: string;
  stepId: string;
  buttonId?: string;
  meta: Record<string, unknown>;
  stepData: Record<string, Record<string, unknown>>;
  snapshot: Record<string, unknown>;
  channel: unknown;
  mode: string;
  updateMeta(updates: Record<string, unknown>): void;
  setStepData(stepId: string, data: Record<string, unknown>): void;
  pipeline: {
    validateAndSubmit(): Promise<{ blocked: boolean; data?: Record<string, unknown>; errors?: Record<string, string[]> }>;
    validateOnly(): Promise<{ blocked: boolean; data?: Record<string, unknown>; errors?: Record<string, string[]> }>;
    collectValues(): Promise<Record<string, unknown>>;
    submitOnly(): Promise<{ blocked: boolean; data?: Record<string, unknown>; errors?: Record<string, string[]> }>;
  };
}

export interface ActionableResult {
  visible: boolean;
  enabled: boolean;
  reason?: string;
}

export type PermissionResolver = (perm: { action: string; resource: string }) => boolean;

type NavigateFunction = (to: string, options?: { state?: unknown }) => void;
type IntentListener = (intent: ActionIntent) => void;
type DomainLoader = () => Promise<unknown>;

const PERMISSION_RESOLVER_KEY = '__WEALTHLANE_PERMISSION_RESOLVER__';
const ACTION_OBJECTS_KEY = '__WEALTHLANE_ACTION_OBJECTS__';
const NAVIGATE_KEY = '__WEALTHLANE_NAVIGATE__';
const INTENT_LISTENERS_KEY = '__WEALTHLANE_INTENT_LISTENERS__';
const DOMAIN_LOADERS_KEY = '__WEALTHLANE_DOMAIN_LOADERS__';
const LOADED_DOMAINS_KEY = '__WEALTHLANE_LOADED_DOMAINS__';
const LOADING_PROMISES_KEY = '__WEALTHLANE_LOADING_PROMISES__';

function getActionObjects(): Map<string, Action> {
  const w = window as any;
  if (!w[ACTION_OBJECTS_KEY]) {
    w[ACTION_OBJECTS_KEY] = new Map<string, Action>();
  }
  return w[ACTION_OBJECTS_KEY] as Map<string, Action>;
}

function getIntentListeners(): Set<IntentListener> {
  const w = window as any;
  if (!w[INTENT_LISTENERS_KEY]) {
    w[INTENT_LISTENERS_KEY] = new Set<IntentListener>();
  }
  return w[INTENT_LISTENERS_KEY] as Set<IntentListener>;
}

function getDomainLoaders(): Record<string, DomainLoader> {
  const w = window as any;
  if (!w[DOMAIN_LOADERS_KEY]) {
    w[DOMAIN_LOADERS_KEY] = {};
  }
  return w[DOMAIN_LOADERS_KEY] as Record<string, DomainLoader>;
}

function getLoadedDomains(): Set<string> {
  const w = window as any;
  if (!w[LOADED_DOMAINS_KEY]) {
    w[LOADED_DOMAINS_KEY] = new Set<string>();
  }
  return w[LOADED_DOMAINS_KEY] as Set<string>;
}

function getLoadingPromises(): Map<string, Promise<unknown>> {
  const w = window as any;
  if (!w[LOADING_PROMISES_KEY]) {
    w[LOADING_PROMISES_KEY] = new Map<string, Promise<unknown>>();
  }
  return w[LOADING_PROMISES_KEY] as Map<string, Promise<unknown>>;
}

class ActionRegistryImpl {
  private _tableAmbientParams: Record<string, string> | undefined;

  /** Scoped params for the active embedded table (e.g. planGrantDocumentId). */
  setTableAmbientParams(
    params: Record<string, unknown> | undefined,
    _tableSemanticId?: string,
  ): void {
    if (params == null || Object.keys(params).length === 0) {
      this._tableAmbientParams = undefined;
      return;
    }
    const merged: Record<string, string> = {};
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== null && value !== '') {
        merged[key] = String(value);
      }
    }
    this._tableAmbientParams = Object.keys(merged).length > 0 ? merged : undefined;
  }

  getTableAmbientParams(): Record<string, string> | undefined {
    return this._tableAmbientParams;
  }

  // ─── Action-object API (OOP pattern — self-registering modules) ──

  /**
   * Register an Action object by its semantic ID.
   * Called at module top-level in self-registering `.actions.ts` files.
   */
  registerAction(action: Action): void {
    const objects = getActionObjects();
    if (objects.has(action.id)) return;
    objects.set(action.id, action);
  }

  /** Resolve an Action object by ID. Returns null if not found. */
  resolveAction(actionId: string): Action | null {
    return getActionObjects().get(actionId) ?? null;
  }

  /** Check if an Action object is registered for this ID. */
  hasAction(actionId: string): boolean {
    return getActionObjects().has(actionId);
  }

  /** Delegate to Action.isAvailable(). Returns false if action not found. */
  isActionAvailable(actionId: string): boolean {
    const action = getActionObjects().get(actionId);
    if (!action) return false;
    return action.isAvailable();
  }

  /** Delegate to Action.isActionable(row). */
  isActionActionable(actionId: string, row: Record<string, unknown>): ActionableResult {
    const action = getActionObjects().get(actionId);
    if (!action) return { visible: false, enabled: false };
    return action.isActionable(row);
  }

  /**
   * Delegate to Action.execute(ctx).
   *
   * Global safety net: if the action throws an unhandled error, it is caught
   * here and routed to `showApiErrorToast` before re-throwing. This ensures
   * users always see feedback for API failures — even when the action author
   * forgets explicit error handling.
   *
   * Actions that already handle errors (via `confirm({ onAction })`,
   * `notify.promise`, or `safeApiCall`) swallow internally and never re-throw,
   * so this catch does NOT produce double toasts — it only fires for truly
   * unhandled errors.
   *
   * Re-throwing preserves existing caller behavior (e.g., FileListNode catch
   * sets download error state, ActionButtonNode finally stops spinner).
   */
  async executeAction(actionId: string, ctx: ActionExecutionContext): Promise<void> {
    const action = getActionObjects().get(actionId);
    if (!action) {
      console.warn(`[ActionRegistry] No Action object for "${actionId}"`);
      return;
    }
    try {
      await action.execute(ctx);
    } catch (error: unknown) {
      showApiErrorToast(error);
      throw error;
    }
  }

  /**
   * Execute an action without the global showApiErrorToast wrapper.
   * Used by useTableSave's saveActionId path where the caller handles
   * error presentation (inline field errors or its own toast).
   */
  async executeActionRaw(actionId: string, ctx: ActionExecutionContext): Promise<void> {
    const action = getActionObjects().get(actionId);
    if (!action) {
      console.warn(`[ActionRegistry] No Action object for "${actionId}"`);
      return;
    }
    await action.execute(ctx);
  }

  // ─── Data actions (view engine integration) ────────────────────

  /**
   * Execute a data action and return its result.
   * Used by the view engine's DataNode to fetch data for a view schema.
   * Constructs a minimal context with only `params` populated.
   */
  async executeData(
    actionId: string,
    params: Record<string, string>,
    data?: Record<string, unknown>,
    opts?: { eventScope?: import('@packages/block-events/core').EventScope; formSemanticId?: string; mode?: FormMode },
  ): Promise<Record<string, unknown>> {
    if (!getActionObjects().has(actionId)) {
      await this.ensureActions([actionId]);
    }
    if (!getActionObjects().has(actionId)) {
      await this.ensureAllDomains();
    }
    const action = getActionObjects().get(actionId);
    if (!action) {
      throw new Error(`[ActionRegistry] No Action object for data action "${actionId}"`);
    }
    const ctx: ActionExecutionContext = {
      pk: '',
      row: {},
      refreshTable: () => {},
      params,
      ...(data != null && { data }),
      ...(opts?.mode != null && { mode: opts.mode }),
      ...(opts?.eventScope != null && { eventScope: opts.eventScope }),
      ...(opts?.formSemanticId != null && { tableSemanticId: opts.formSemanticId }),
    };
    const result = await action.execute(ctx);
    return (result as Record<string, unknown>) ?? {};
  }

  /**
   * Execute a data action with a typed payload.
   * Designed for wizard handlers and non-table callers that need to invoke
   * existing form/data actions without constructing fake table context.
   *
   * Returns the action result directly — callers handle notifications and cache.
   */
  async executeSubmit(
    actionId: string,
    payload: unknown,
    opts?: { meta?: Record<string, unknown>; mode?: FormMode },
  ): Promise<Record<string, unknown>> {
    if (!getActionObjects().has(actionId)) {
      await this.ensureActions([actionId]);
    }
    if (!getActionObjects().has(actionId)) {
      await this.ensureAllDomains();
    }
    const action = getActionObjects().get(actionId);
    if (!action) {
      throw new Error(`[ActionRegistry] No Action object for submit action "${actionId}"`);
    }
    const ctx: ActionExecutionContext = {
      pk: '',
      row: {},
      refreshTable: () => {},
      params: { payload } as unknown as Record<string, string>,
      ...(opts?.meta != null && { meta: opts.meta }),
      ...(opts?.mode != null && { mode: opts.mode }),
    };
    const result = await action.execute(ctx);
    return (result as Record<string, unknown>) ?? {};
  }

  /**
   * Execute a builder action with full context (params, meta).
   * Used by ActionBlockRenderer — builder actions return { $descriptor: true, block: ... }.
   */
  async executeBuilder(
    actionId: string,
    context: { params?: Record<string, string>; meta?: Record<string, unknown> },
  ): Promise<Record<string, unknown>> {
    if (!getActionObjects().has(actionId)) {
      await this.ensureActions([actionId]);
    }
    if (!getActionObjects().has(actionId)) {
      await this.ensureAllDomains();
    }
    const action = getActionObjects().get(actionId);
    if (!action) {
      throw new Error(`[ActionRegistry] No Action object for builder action "${actionId}"`);
    }
    const resolvedParams = context.params ?? {};
    const ctx: ActionExecutionContext = {
      pk: resolvedParams.id ?? '',
      row: {},
      refreshTable: () => {},
      params: resolvedParams,
      meta: context.meta,
    };
    const result = await action.execute(ctx);
    return (result as Record<string, unknown>) ?? {};
  }

  // ─── Domain loaders (convention-based auto-loading) ─────────────

  /**
   * Extract the domain from an action ID.
   * Strategy: try last word first, then second-to-last word.
   * This handles both `VERB_DOMAIN` (e.g. VIEW_PARTICIPANT → participant)
   * and `VERB_DOMAIN_NOUN` (e.g. BUILD_PARTICIPANT_TABS → participant).
   */
  getDomain(actionId: string): string {
    const parts = actionId.toLowerCase().split('_');
    return parts[parts.length - 1];
  }

  private getDomainCandidates(actionId: string): string[] {
    const parts = actionId.toLowerCase().split('_');
    const candidates: string[] = [];
    if (parts.length >= 1) candidates.push(parts[parts.length - 1]);
    if (parts.length >= 3) candidates.push(parts[parts.length - 2]);
    return candidates;
  }

  /** Register a domain loader. One line per domain. */
  registerDomainLoader(domain: string, loader: DomainLoader): void {
    getDomainLoaders()[domain] = loader;
  }

  /**
   * Ensure all actions for the given IDs are loaded.
   * Called by DynamicTableV2 during schema init.
   * Derives domain from each ID, loads unloaded domains in parallel.
   */
  async ensureActions(actionIds: string[]): Promise<void> {
    const domainsToLoad = new Set<string>();
    const loadedDomains = getLoadedDomains();
    const loaders = getDomainLoaders();

    for (const id of actionIds) {
      if (getActionObjects().has(id)) continue;
      const candidates = this.getDomainCandidates(id);
      for (const domain of candidates) {
        if (!loadedDomains.has(domain) && loaders[domain]) {
          domainsToLoad.add(domain);
        }
      }
    }

    if (domainsToLoad.size === 0) return;

    const loadingPromises = getLoadingPromises();

    await Promise.all(
      [...domainsToLoad].map((domain) => {
        loadedDomains.add(domain);
        if (!loadingPromises.has(domain)) {
          const promise = loaders[domain]();
          loadingPromises.set(domain, promise);
        }
        return loadingPromises.get(domain)!;
      }),
    );
  }

  /**
   * Load a specific domain's actions by folder name.
   * Used by compose/form block renderers that know the entity domain (e.g. "participant")
   * but the data action IDs (e.g. FETCH_EMPLOYEE_TYPES) don't map to that domain
   * via the last-word convention.
   */
  async ensureDomain(domain: string): Promise<void> {
    const loadedDomains = getLoadedDomains();
    const loadingPromises = getLoadingPromises();

    if (loadedDomains.has(domain)) {
      const inflight = loadingPromises.get(domain);
      if (inflight) await inflight;
      return;
    }

    const loaders = getDomainLoaders();
    if (!loaders[domain]) return;

    loadedDomains.add(domain);
    if (!loadingPromises.has(domain)) {
      const promise = loaders[domain]();
      loadingPromises.set(domain, promise);
    }
    await loadingPromises.get(domain)!;
  }

  /**
   * Load ALL unloaded domain modules. Used as a fallback when an action ID
   * cannot be resolved via the last-word heuristic (e.g. data actions like
   * FETCH_EMPLOYEE_TYPES whose suffix "types" doesn't match any domain folder).
   * Only triggers lazy imports for domains not yet loaded — subsequent calls are no-ops.
   */
  async ensureAllDomains(): Promise<void> {
    const loadedDomains = getLoadedDomains();
    const loaders = getDomainLoaders();
    const loadingPromises = getLoadingPromises();

    const pending: Promise<unknown>[] = [];

    for (const domain of Object.keys(loaders)) {
      if (!loadedDomains.has(domain)) {
        loadedDomains.add(domain);
        if (!loadingPromises.has(domain)) {
          const promise = loaders[domain]();
          loadingPromises.set(domain, promise);
        }
      }
      const p = loadingPromises.get(domain);
      if (p) pending.push(p);
    }

    if (pending.length > 0) await Promise.all(pending);
  }

  // ─── Navigation (set once by AppShell) ──────────────────────────

  setNavigate(fn: NavigateFunction): void {
    (window as any)[NAVIGATE_KEY] = fn;
  }

  getNavigate(): NavigateFunction {
    const fn = (window as any)[NAVIGATE_KEY];
    if (!fn) {
      console.warn('[ActionRegistry] navigate not set. Call ActionRegistry.setNavigate() in AppShell.');
      return () => {};
    }
    return fn as NavigateFunction;
  }

  // ─── Intent system (Mediator Pattern for modal actions) ─────────

  emitIntent(intent: ActionIntent): void {
    const listeners = getIntentListeners();
    for (const listener of listeners) {
      listener(intent);
    }
  }

  /** Subscribe to intents. Returns an unsubscribe function. */
  onIntent(listener: IntentListener): () => void {
    const listeners = getIntentListeners();
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }

  // ─── Permission resolver (shared) ──────────────────────────────

  setPermissionResolver(resolver: PermissionResolver): void {
    (window as any)[PERMISSION_RESOLVER_KEY] = resolver;
  }

  getPermissionResolver(): PermissionResolver | null {
    return (window as any)[PERMISSION_RESOLVER_KEY] ?? null;
  }

  clear(): void {
    getActionObjects().clear();
    getIntentListeners().clear();
    getLoadedDomains().clear();
    getLoadingPromises().clear();
    this._tableAmbientParams = undefined;
  }

  __debugKeys(): string[] {
    return Array.from(getActionObjects().keys()).map((k) => `[Action] ${k}`);
  }
}

export const ActionRegistry = new ActionRegistryImpl();
